import LoadingScreen from 'components/loading-screen'
import { useContext, useEffect, useState } from 'react'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { LoaderContext } from 'contexts/loader-context'
import { useRouteMatch } from 'react-router-dom'
import { useSession } from 'contexts/session-context'
import useRouter from 'hooks/use-router'
import SurveyService from 'services/survey.services'
import { useTrivia } from '../../contexts/trivia-context'
import TriviaModal from './components/modal'
import TriviaGame from './components/trivia-game'
import PopUpError from './components/popUpError'
import {
  PREVIOUS_SURVEY_ANSWERED_CODE,
  buildPreviousSurveyPath,
  buildTriviaReturnPath,
  isIntroAccepted,
  needsPreviousSurvey
} from './previous-survey'

interface IProps {
  isDirectAccess?: boolean
}

const TriviaChallenge = ({ isDirectAccess = false }: IProps) => {
  const [{ error, currentTrivia }, { validateTrivia, resetTrivia }] = useTrivia()
  const [{ data }] = useSession()
  const route = useRouteMatch<{ id: string }>()
  const { pathname, search, query, push } = useRouter()
  const [userAccepted, setUserAccepted] = useState(() => isIntroAccepted(query))
  const { setLoad } = useContext(LoaderContext)
  const [executed, setExecuted] = useState(false)
  const [checkingSurvey, setCheckingSurvey] = useState(false)

  /**
   * Checks the previous survey directly against the service (not through the
   * survey context) so the shared survey state is not mutated from the trivia.
   * Fails open: any unexpected error is treated as "answered" so the trivia is
   * never blocked and no redirect loop can be created.
   */
  const isPreviousSurveyAnswered = async (surveyId: number): Promise<boolean> => {
    try {
      const response = await SurveyService.validateSurvey({ surveyId, isDirectAccess: false, session: data })
      return response.data?.isAnswered === true
    } catch (error) {
      if (error?.response?.data?.code === PREVIOUS_SURVEY_ANSWERED_CODE) return true
      console.error('previous survey validate err', error)
      return true
    }
  }

  const triviaValidate = async (id: any) => {
    try {
      setLoad(true)
      await validateTrivia(id, isDirectAccess)
    } catch (error) {
      console.error(error)
    } finally {
      setLoad(false)
    }
  }

  useEffect(() => {
    if (data && !executed) {
      triviaValidate(route.params.id)
      setExecuted(!executed)
    }
  }, [data])

  const onAccept = async () => {
    gtag('event', 'event_to_ga', { event_category: "Trivias", event_label: `Aceptar Jugar Trivia`, value: route.params.id })
    dlTrackEvent('user_interaction', { action: 'click', element: 'comienza a jugar', section: `trivia :: ${route.params.id}` })

    const previousSurveyId = needsPreviousSurvey(currentTrivia)
    if (!previousSurveyId) return setUserAccepted(true)

    // While the survey is being checked the modal is unmounted, so the button cannot be tapped twice
    setCheckingSurvey(true)
    const answered = await isPreviousSurveyAnswered(previousSurveyId)

    if (answered) {
      setCheckingSurvey(false)
      return setUserAccepted(true)
    }

    dlTrackEvent('user_interaction', { action: 'redirect', element: 'previous survey', section: `trivia :: ${route.params.id}` })
    resetTrivia()
    return push(buildPreviousSurveyPath(previousSurveyId, buildTriviaReturnPath(pathname, search)))
  }

  if (error) {
    return <PopUpError errorCode={error} />
  }

  if (checkingSurvey || !currentTrivia) {
    return <LoadingScreen />
  }

  if (!userAccepted) {
    return <TriviaModal onAccept={onAccept} />
  }

  return <TriviaGame id={route.params.id} />
}

export default TriviaChallenge
